import { registerDestructor } from '@ember/destroyable';

import Modifier from 'ember-modifier';

import type { CodeData } from '@cardstack/host/lib/formatted-message/utils';

import type { MonacoSDK } from '@cardstack/host/services/monaco-service';
import { createMonacoWaiterManager } from '@cardstack/host/utils/editor/monaco-test-waiter';

import type { MonacoEditorOptions } from './monaco';

import type * as _MonacoSDK from 'monaco-editor';

export const commonEditorOptions: MonacoEditorOptions = {
  theme: 'vs-dark',

  automaticLayout: true,
  fontSize: 12,
  fontFamily: 'IBM Plex Mono',
  fontWeight: '500',
  lineNumbers: 'off',
  readOnly: true,
  scrollbar: {
    alwaysConsumeMouseWheel: false,
  },
  scrollBeyondLastLine: false,
};

export interface MonacoEditorSignature {
  Args: {
    Named: {
      codeData?: Partial<CodeData>;
      monacoSDK: MonacoSDK;
      editorDisplayOptions: MonacoEditorOptions;
    };
  };
}

export default class MonacoEditorModifier extends Modifier<MonacoEditorSignature> {
  private monacoState: {
    editor: _MonacoSDK.editor.IStandaloneCodeEditor;
    disposables: _MonacoSDK.IDisposable[];
  } | null = null;
  private hasDestructor = false;
  private waiterManager = createMonacoWaiterManager();
  modify(
    element: HTMLElement,
    _positional: [],
    {
      codeData,
      monacoSDK,
      editorDisplayOptions,
    }: MonacoEditorSignature['Args']['Named'],
  ) {
    if (!codeData) {
      return;
    }

    let { code, language } = codeData;
    if (!code || !language) {
      return;
    }
    if (this.monacoState) {
      let { editor } = this.monacoState;
      let model = editor.getModel()!;

      // Here we are appending deltas when code is "streaming" in, which is
      // useful when code changes frequently in short periods of time. In this
      // case we calculate the delta of the new code and the current code, and
      // then apply that delta to the model. Compared to calling setValue()
      // for every new value, this removes the need for re-tokenizing the code
      // which is expensive and produces visual annoyances such as flickering.

      let currentCode = model.getValue();
      let newCode = code ?? '';

      if (!newCode.startsWith(currentCode)) {
        // This is a safety net for rare cases where the new code streamed in
        // does not begin with the current code. This can happen when streaming
        // in code with search/replace diff markers and the diff marker in chunk
        // is incomplete, for example "<<<<<<< SEAR" instead of
        // "<<<<<<< SEARCH". In this case the code diff parsing logic
        // in parseCodeContent will not recognize the diff marker and it will
        // display "<<<<<<< SEAR" for a brief moment in the editor, before getting
        // a chunk with a complete diff marker. In this case we need to reset
        // the data otherwise the appending delta will be incorrect and we'll
        // see mangled code in the editor (syntax errors with incomplete diff markers).
        model.setValue(newCode);
      } else {
        let codeDelta = newCode.slice(currentCode.length);

        let lineCount = model.getLineCount();
        let lastLineLength = model.getLineLength(lineCount);

        let range = {
          startLineNumber: lineCount,
          startColumn: lastLineLength + 1,
          endLineNumber: lineCount,
          endColumn: lastLineLength + 1,
        };

        let editOperation = {
          range: range,
          text: codeDelta,
          forceMoveMarkers: true,
        };

        let withDisabledReadOnly = (
          readOnlySetting: boolean,
          fn: () => void,
        ) => {
          editor.updateOptions({ readOnly: false });
          fn();
          editor.updateOptions({ readOnly: readOnlySetting });
        };

        withDisabledReadOnly(!!editorDisplayOptions.readOnly, () => {
          editor.executeEdits('append-source', [editOperation]);
        });

        editor.revealLine(lineCount + 1); // Scroll to the end as the code streams
      }
    } else {
      let monacoContainer = element;

      let editor = monacoSDK.editor.create(
        monacoContainer,
        editorDisplayOptions,
      );

      let model = editor.getModel()!;
      monacoSDK.editor.setModelLanguage(model, language);

      model.setValue(code);

      const contentHeight = editor.getContentHeight();
      if (contentHeight > 0) {
        element.style.height = `${contentHeight}px`;
      }

      let disposables: _MonacoSDK.IDisposable[] = [];
      disposables.push(
        editor.onDidContentSizeChange(() => {
          const newHeight = editor.getContentHeight();
          if (newHeight > 0) {
            element.style.height = `${newHeight}px`;
          }
        }),
      );

      this.monacoState = {
        editor,
        disposables,
      };

      // Track editor initialization for test waiters after models and language are ready
      if (this.waiterManager) {
        const operationId = `monaco-editor-modifier-init-${editor.getId()}`;
        this.waiterManager.trackEditorInit(editor, operationId);
      }
    }

    if (!this.hasDestructor) {
      this.hasDestructor = true;
      registerDestructor(this, () => {
        let editor = this.monacoState?.editor;
        let model = editor?.getModel();
        let disposables = this.monacoState?.disposables;
        if (disposables) {
          for (let d of disposables) {
            try {
              d.dispose();
            } catch {
              // listener disposal during teardown races with Monaco's own
              // dispose; ignore so one bad listener doesn't block the rest
            }
          }
          disposables.length = 0;
        }
        if (editor) {
          this.disposeEditorAfterInitialLayout(editor, model);
        }
        this.monacoState = null;
      });
    }
  }

  private disposeEditorAfterInitialLayout(
    editor: _MonacoSDK.editor.IStandaloneCodeEditor,
    model: _MonacoSDK.editor.ITextModel | null | undefined,
  ) {
    // Monaco contribution setup can continue past initial layout into the next
    // paint. When a streaming code block is immediately replaced by a diff
    // block, disposing in the same render turn can race that bootstrap and
    // throw "InstantiationService has been disposed". Unlike the operator-mode
    // Monaco modifiers, this code-block variant is used inside streaming AI
    // messages where rapid render/teardown cycles reliably hit that race, so
    // we defer to the next paint even in tests rather than disposing
    // synchronously.
    // eslint-disable-next-line @cardstack/boxel/no-raf-for-state -- Monaco dispose must wait for paint to avoid bootstrap race
    requestAnimationFrame(() => {
      try {
        editor.dispose();
      } catch {
        // partially-instantiated editor — best-effort cleanup
      }
      if (model && !model.isDisposed() && !model.isAttachedToEditor()) {
        model.dispose();
      }
    });
  }
}
